import moment from "moment"
import {
  isValidElement,
  type ReactElement,
  type ReactNode,
  Children,
} from "react"

import { ButtonProps, ContentfulButton } from "@/ui/components/Button"

export const categoriesToExclude = ["featured"]

export const BREAKPOINT_KEYS = ["xs", "sm", "md", "lg", "xl"] as const

export type BreakpointSizes = (typeof BREAKPOINT_KEYS)[number]

export type Breakpoints = Record<BreakpointSizes, number>

export const screenSizes: Breakpoints = {
  xs: 320,
  sm: 584,
  md: 768,
  lg: 1024,
  xl: 1280,
}
/**
 * Grabs the current Window URL and appends URI for the full URL.
 * @param uri
 */
export const fullURLfromURI = (uri: string) => {
  if (typeof window === "undefined") {
    return uri
  }

  if (!uri.startsWith("/")) {
    return uri
  }

  const getUrl = window.location
  return `${getUrl.protocol}//${getUrl.host}${uri}`
}

/**
 * Removes trailing slashes from a string.
 * @param str
 * @returns {string}
 */

export const removeTrailingSlashes = (
  str: string | null | undefined,
): string | false => str && typeof str === "string" && str.replace(/\/+$/, "")

export function stripHtml(html?: string) {
  if (!html) {
    return html
  }

  if (typeof document === "undefined") {
    return html
  }

  const tmp = document.createElement("DIV")
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ""
}

/**
 * Function to sort ids numericallys
 *
 * @param data
 * @param ids
 */
export function sortDataByIds(data, ids) {
  return [...data].filter(Boolean).sort((a, b) => {
    const indexA = ids.indexOf(a.sys.id)
    const indexB = ids.indexOf(b.sys.id)
    return indexA - indexB
  })
}

/**
 *Runs a callback if in a specific environment
 *
 * @param testEnv
 * @param callback
 */
export const runIfEnv = (
  testEnv: "development" | "production" | "test",
  callback: () => void,
) => {
  const nodeEnv = process.env.NODE_ENV // Standard env
  const vercelEnv = process.env.VERCEL_ENV // Vercel-specific env

  // Map Vercel environments to match the common NODE_ENV values
  const mappedVercelEnv = vercelEnv === "preview" ? "development" : vercelEnv

  if ([mappedVercelEnv, nodeEnv].includes(testEnv)) {
    callback?.()
  }
}

/**
 * Removes specific trailing ("image_crop_resized") from Wistia thumbnails
 *
 * @param url
 */
export const removeImageCropResize = (url: string) => {
  try {
    // Parse the URL.
    const urlObj = new URL(url)
    // Check if the parameter exists.
    if (urlObj.searchParams.has("image_crop_resized")) {
      // Remove the parameter.
      urlObj.searchParams.delete("image_crop_resized")
    }
    // Return the modified URL as a string.
    return urlObj.toString()
  } catch (e) {
    // If URL constructor fails (e.g., invalid URL), fallback to a regex approach.
    return url.replace(/[?&]image_crop_resized=[^&]+/, "")
  }
}

export const capitalizeWord = (word: string) =>
  word.charAt(0).toUpperCase() + word.slice(1)

/**
 * Formats a given date using the specified format string.
 * If no format string is provided, it defaults to 'YYYY-MM-DD HH:mm:ss'.
 *
 * a JavaScript Date object, a Unix timestamp (in milliseconds),
 * an ISO 8601 string, or any other input that Moment.js can parse.
 * @param isoDateString
 * @param {string} [format='YYYY-MM-DD HH:mm:ss'] - The desired format string. See the
 * {@link https://momentjs.com/docs/#/displaying/format/|Moment.js formatting documentation}
 * for available tokens.
 * @returns {string} The formatted date string, or an empty string if the input date is invalid.
 *
 * @example
 * ```javascript
 * const now = moment();
 * const formattedNow = formatDate(now); // Returns something like "2025-05-14 17:06:00"
 *
 * const someDate = new Date(2026, 0, 15);
 * const formattedDate = formatDate(someDate, 'MM/DD/YYYY'); // Returns "01/15/2026"
 *
 * const invalidDate = 'not a date';
 * const formattedInvalidDate = formatDate(invalidDate); // Returns ""
 * ```
 */
export const formatDateWithMoment = (
  isoDateString: string,
  format: string = "D MMM YYYY",
): string | null => {
  try {
    const date = moment.utc(isoDateString)

    if (!date.isValid()) {
      runIfEnv("development", () => {
        console.error(`Invalid date string provided: ${isoDateString}`)
        return null
      })
    }

    return date.format(format)
  } catch (error) {
    console.error(
      `An error occurred while formatting the date with Moment.js: ${error.message}`,
    )
    return null
  }
}

/**
 * Determines the aspect ratio of an image by comparing the height and width
 *
 * Useful for determining whether there should be a hard limit on an asset size based on the dimensions provided
 */
export const determineAspectRatio = ({
  width,
  height,
}: {
  width: number
  height: number
}): "landscape" | "portrait" | "square" => {
  if (width > height) {
    return "landscape"
  }
  if (height > width) {
    return "portrait"
  }

  return "square"
}

// This function maps the Contentful Button component to the codebase Button Component
export const mapContentfulToButtonProps = (
  button: ContentfulButton,
): ButtonProps => ({
  to: button.ctaLink,
  children: button.ctaText,
  theme: button.theme,
  size: button.size,
  variant: button.variant,
  type: button.type,
})

export const hasChildren = (
  element: ReactNode,
): element is ReactElement<{ children: ReactNode | ReactNode[] }> =>
  isValidElement<{ children?: ReactNode[] }>(element) &&
  Boolean(element.props.children)

export const childToString = (child?: ReactNode): string => {
  if (
    typeof child === "undefined" ||
    child === null ||
    typeof child === "boolean"
  ) {
    return ""
  }

  if (JSON.stringify(child) === "{}") {
    return ""
  }

  return child.toString()
}

export const onlyText = (children: ReactNode | ReactNode[]): string => {
  if (!(children instanceof Array) && !isValidElement(children)) {
    return childToString(children)
  }

  return Children.toArray(children).reduce<string>(
    (text: string, child: ReactNode): string => {
      let newText = ""

      if (hasChildren(child)) {
        newText = onlyText(child.props.children)
      } else if (isValidElement(child)) {
        newText = ""
      } else {
        newText = childToString(child)
      }

      return text.concat(newText)
    },
    "",
  )
}
